/**
 * ExpandedOrderContent — the body of an expanded Tracker order row.
 *
 * Extracted from `TrackerOrderTable` (BLU-758) so the same panel shell, the
 * same skeleton and the same error/empty states serve both Tracker tables
 * instead of being re-typed per surface. `TrackerQueueTable` renders pre-batch
 * line items (no garments exist before batching) so it keeps its own body, but
 * it now uses the same `ExpandedRowPanel` / `ExpandedRowMessage` shells — the
 * part that WAS duplicated.
 *
 * The error state gained a Retry button in the same pass. The old copy said
 * "click to collapse and try again", which is a dead end dressed up as
 * guidance: collapsing does not refetch until the 30s cache expires.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { GitBranch, RotateCw, FileWarning, Package, RotateCcw, ArrowLeftRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useDateFnsLocale } from '@/lib/format-dates';
import type { RouterOutputs } from '@/lib/trpc';
import { useOrderGarments } from '../hooks/useProductionTracker';
import { STATUS_LABEL_KEYS, statusColors } from '../lib/order-status-display';
import { TrackerGarmentTable } from './TrackerGarmentTable';
import { SwitchOrderPrintTechnologyDialog } from './SwitchOrderPrintTechnologyDialog';
import { orderSwitchDirections } from '../lib/print-technology-switch';
import { useStationSettings } from '@/features/production-settings/hooks/useStationSettings';
import type { PrintTechnology } from '@/features/production-orders/utils/print-tech-routing';
import { useRole } from '@/hooks/use-role';
import { canOpenIssuesPage } from '@/lib/roles';
import { useLocation } from '@/contexts/LocationContext';
import { useCanManageFloor } from '../hooks/useCanManageFloor';
import { ClientQcRedoDialog } from './ClientQcRedoDialog';
import { qcRedoEligibleGarments } from '../lib/client-qc-redo';

/**
 * One of the order's OTHER live fulfilment-order parts, as returned by
 * `productionOrders.getOrderGarments`. Nullable fields are REDACTED (not
 * missing) for floors this caller has no relationship with — render around
 * them, never render "null".
 */
export type TrackerOrderSibling =
  RouterOutputs['productionOrders']['getOrderGarments']['siblings'][number];

// ============================================================================
// SHARED SHELLS
// ============================================================================

/** The indent + rhythm every expanded Tracker row body sits in. */
export function ExpandedRowPanel({ children }: { children: React.ReactNode }) {
  return <div className="pl-12 pr-4 py-2">{children}</div>;
}

/**
 * A single-sentence state (loading failed / nothing here) inside an expanded
 * row, with an optional action beside it. Inline, not modal: the modal + audio
 * rule in PRODUCTION_DESIGN.md §6 covers scanner stations where an operator is
 * mid-task and cannot read fine print — the Tracker is a seated monitoring
 * surface, and a modal for "this sub-table didn't load" would be noise.
 */
export function ExpandedRowMessage({
  tone = 'muted',
  children,
  action,
}: {
  tone?: 'muted' | 'error';
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    // pl-12 pr-4 matches ExpandedRowPanel exactly: the message and the table it
    // stands in for are the same slot, so a load failure must not shunt the text
    // to a different indent than the content it replaced.
    <div className="flex flex-wrap items-center gap-3 py-4 pl-12 pr-4">
      {/* role="status" (polite), never "alert": this replaces content the
          operator just asked for, and it also announces the result of pressing
          Retry. Assertive would interrupt them mid-sentence for a sub-table
          (PRODUCTION_DESIGN.md §13). Scoped to the MESSAGE, not the wrapper —
          with the button inside the live region, every re-render of a busy
          Retry re-announces "Retry" alongside the sentence. */}
      <p
        role="status"
        className={cn('text-sm', tone === 'error' ? 'text-destructive' : 'text-muted-foreground')}
      >
        {children}
      </p>
      {action}
    </div>
  );
}

/**
 * BLU-817: the amber twin of TrackerOrderRow's own Hybrid callout (same
 * shape — border + bg + icon + text-base title + text-sm body — cloned from
 * TrackerOrderRow.tsx), extracted here because BOTH Tracker surfaces
 * (batch-view sub-row and this Orders-view body) need it verbatim — the same
 * reason ExpandedRowPanel/ExpandedRowMessage above were extracted. Amber, not
 * the Hybrid callout's blue: an open print-file issue is a hard block
 * (PRODUCTION_DESIGN.md §4.1 — the garment cannot be filmed until it's
 * fixed), an actionable problem rather than routing info, so callers render
 * it BEFORE any other callout on the row. A visible sentence, never a
 * title/tooltip (§10).
 */
export function PrintFileIssueCallout({ count }: { count: number }) {
  const { t } = useTranslation('production');
  const { role } = useRole();
  return (
    <div className="my-2 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <FileWarning className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <div>
        <p className="text-base font-bold text-amber-800 dark:text-amber-200">
          {t('tracker.printFileIssue.calloutTitle')}
        </p>
        <p className="text-sm text-amber-700 dark:text-amber-300">
          {t('tracker.printFileIssue.calloutBody', { count })}
        </p>
        {/* The tracker admits Picker/Printer/Catcher/Packer; /production/issues does
            not. For them this link is a silent RoleGuard <Navigate> back to their
            station home — they'd lose the tracker and the row they were reading, with
            no explanation. Give those roles the station's escalation voice instead. */}
        {canOpenIssuesPage(role) ? (
          <Link
            to="/production/issues?tab=print-files"
            className="mt-1 inline-flex items-center text-sm font-medium text-amber-800 underline dark:text-amber-200"
          >
            {t('tracker.printFileIssue.viewInIssues')}
          </Link>
        ) : (
          <p className="mt-1 text-sm font-medium text-amber-800 dark:text-amber-200">
            {t('tracker.printFileIssue.tellCoordinator')}
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SIBLING (SPLIT-ORDER) SUMMARY
// ============================================================================

function SiblingLine({ sibling }: { sibling: TrackerOrderSibling }) {
  const { t } = useTranslation('production');
  const locale = useDateFnsLocale();

  const statusKey = STATUS_LABEL_KEYS[sibling.production_status];
  const hasShipmentDetail = !!sibling.shipped_at || !!sibling.tracking_number;
  const hasSecondLine = hasShipmentDetail || sibling.batch_number !== null;

  return (
    <li className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Badge
          variant="secondary"
          className={cn('shrink-0', statusColors[sibling.production_status] ?? statusColors.NEW)}
        >
          {statusKey ? t(statusKey) : sibling.production_status.replace(/_/g, ' ')}
        </Badge>
        {/* Redacted floor: the caller has no relationship with that location, so
            its NAME never leaves the server. Say so plainly — an em dash or a
            blank would read as missing data. text-base, not the table's text-xs:
            this is the line the operator opened the row to read
            (PRODUCTION_DESIGN.md §4.2). */}
        <span className={cn('text-base font-medium', !sibling.location_name && 'text-muted-foreground')}>
          {sibling.location_name ?? t('tracker.orders.split.otherLocation')}
        </span>
        <span className="text-sm text-muted-foreground" aria-hidden="true">·</span>
        <span className="text-sm text-muted-foreground tabular-nums">
          {t('itemCount', { count: sibling.item_count })}
        </span>
      </div>
      {hasSecondLine && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
          {sibling.shipped_at && (
            <span>
              {t('tracker.orders.split.shipped', {
                time: formatDistanceToNow(new Date(sibling.shipped_at), { addSuffix: true, locale }),
              })}
            </span>
          )}
          {sibling.tracking_number && (
            <span className="inline-flex items-baseline gap-1.5">
              {t('tracker.orders.split.tracking')}
              {/* Mirrors ShippingPanel's treatment: mono + break-all, because a
                  tracking number is read digit by digit and must not be
                  truncated. Carrier rides alongside, not on its own line. */}
              <span className="font-mono break-all text-foreground">{sibling.tracking_number}</span>
              {sibling.carrier && (
                <span>
                  <span aria-hidden="true">· </span>
                  {sibling.carrier}
                </span>
              )}
            </span>
          )}
          {sibling.batch_number !== null && (
            // Reads as the last clause of the sentence, not a right-aligned
            // column: `ml-auto` looked tidy on one line and stranded the ref
            // alone against the far edge the moment the tracking number pushed
            // it to a second row. Same muted BATCH-000 shape as the Batch column
            // above, so an operator can match the two by eye.
            <span className="font-mono">
              BATCH-{String(sibling.batch_number).padStart(3, '0')}
            </span>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * BLU-758: "the rest of this order". A split order's tracker row shows ONE
 * fulfilment-order part, so its item count is short by design — without this
 * block an operator reads the shortfall as a picking error and re-picks.
 *
 * Placed ABOVE the garment table for that reason (same precedent as
 * `TrackerOrderRow`'s Hybrid callout): it is the explanation for why the table
 * below is short. Neutral border/muted, NOT the Hybrid callout's blue — the two
 * can appear on the same row, and two blue callouts stacked read as one blob.
 * Each sibling's own status pill carries the semantic colour.
 */
function SiblingSummary({
  siblings,
  expectSiblings,
}: {
  siblings: TrackerOrderSibling[];
  expectSiblings: boolean;
}) {
  const { t } = useTranslation('production');

  if (siblings.length === 0 && !expectSiblings) return null;

  return (
    <section className="my-2 rounded-lg border bg-muted/30 p-3">
      {/* Styled as a heading, not marked up as one. The Tracker page has a
          single h1 and no h2/h3, so an <h4> here injects a two-level jump into
          the document outline and lands this block under nothing when a screen
          reader lists headings. It is a label on a callout inside a table row,
          not a section of the page. */}
      <p className="flex items-center gap-2 text-base font-semibold">
        <GitBranch className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        {t('tracker.orders.split.othersTitle')}
      </p>
      {siblings.length === 0 ? (
        // The row badge promised siblings and the fetch found none — a part was
        // cancelled between the two reads. Say that, rather than showing an
        // empty box that looks like a rendering bug.
        <p className="mt-1.5 text-sm text-muted-foreground">
          {t('tracker.orders.split.noneFound')}
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {siblings.map((s) => (
            <SiblingLine key={s.id} sibling={s} />
          ))}
        </ul>
      )}
    </section>
  );
}

// ============================================================================
// EXPANDED ORDER CONTENT
// ============================================================================

export function ExpandedOrderContent({
  orderId,
  orderName,
  expectSiblings = false,
  onClientQcRedoDone,
}: {
  orderId: string;
  /** BLU-1462: names the order in the Client QC redo dialog. */
  orderName?: string;
  /** BLU-1462: after a client QC redo reopens the order (switch to Active + search it). */
  onClientQcRedoDone?: (orderName: string) => void;
  /**
   * True when the collapsed row showed a Split badge, i.e. the list payload
   * counted ≥2 live parts. Lets the summary distinguish "not a split order"
   * (render nothing) from "split, but the siblings came back empty" (say so).
   */
  expectSiblings?: boolean;
}) {
  const { t } = useTranslation('production');
  const { data, isLoading, error, refetch, isRefetching } = useOrderGarments(orderId);
  // BLU-1462: Client QC redo is the floor owner's call on a handed-over
  // order (same gate as the print technology switch, computed once here and
  // passed to TrackerGarmentTable). The server's owner-floor fence refuses
  // anyone else anyway.
  const canManageFloor = useCanManageFloor();
  const { selectedLocation } = useLocation();
  const [qcRedoOpen, setQcRedoOpen] = useState(false);
  const canClientQcRedo =
    canManageFloor && data?.qcRedo?.handedOver === true && data?.qcRedo?.open !== true;
  // BLU-1509: the Orders view's "Switch order to ..." buttons, the same
  // directions the Batches view's order menu offers (one per direction any
  // loaded garment can still go).
  const { settings: stationSettings } = useStationSettings({ enabled: canManageFloor });
  const [orderSwitchTo, setOrderSwitchTo] = useState<PrintTechnology | null>(null);
  const loadedGarments = data?.garments;
  const switchTargets = useMemo(
    () => (canManageFloor && loadedGarments ? orderSwitchDirections(loadedGarments, stationSettings.dtfEnabled) : []),
    [canManageFloor, loadedGarments, stationSettings.dtfEnabled],
  );

  if (isLoading) {
    return (
      // Same pl-12 pr-4 indent as ExpandedRowPanel / ExpandedRowMessage: the
      // skeleton stands where the table will land, so the content must not slide
      // sideways when it arrives.
      <div className="py-4 pl-12 pr-4 space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-16 ml-auto" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ExpandedRowMessage
        tone="error"
        action={
          // aria-busy, NOT disabled. Disabling on click drops keyboard focus to
          // <body>, so a keyboard operator loses their place inside the expanded
          // row and has to tab back in from the top of the table to press Retry
          // again. The spinner + aria-busy say "working" without moving focus;
          // a second press while in flight is deduped by react-query anyway.
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            aria-busy={isRefetching}
          >
            <RotateCw
              className={cn('h-4 w-4', isRefetching && 'motion-safe:animate-spin')}
              aria-hidden="true"
            />
            {t('common:retry')}
          </Button>
        }
      >
        {t('tracker.orders.failedToLoadGarments')}
      </ExpandedRowMessage>
    );
  }

  const siblings = data?.siblings ?? [];
  const garments = data?.garments ?? [];
  const packages = data?.packages ?? [];
  const printFileIssueCount = garments.filter((g) => g.hasOpenPrintFileIssue).length;

  // Siblings still render when the garment list is empty: "no garments here,
  // but the other part shipped yesterday" is the whole answer an operator
  // opened this row for.
  if (garments.length === 0 && siblings.length === 0 && packages.length === 0 && !expectSiblings) {
    return <ExpandedRowMessage>{t('tracker.orders.noGarments')}</ExpandedRowMessage>;
  }

  return (
    <ExpandedRowPanel>
      {printFileIssueCount > 0 && <PrintFileIssueCallout count={printFileIssueCount} />}
      <SiblingSummary siblings={siblings} expectSiblings={expectSiblings} />
      {packages.length > 0 && (
        <section className="my-2 rounded-lg border bg-muted/30 p-3">
          <p className="mb-2 flex items-center gap-2 text-base font-semibold">
            <Package className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {t('tracker.orders.packages.title', { count: packages.length })}
          </p>
          <ul className="space-y-2">
            {packages.map((pkg) => (
              <li key={pkg.id} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono break-all">{pkg.trackingNumber}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {t(`tracker.orders.packages.contents.${pkg.contentsState}`)}
                  </Badge>
                  {pkg.carrier && <span className="text-muted-foreground">{pkg.carrier}</span>}
                </div>
                <p className="text-muted-foreground">
                  {pkg.extractedSkus.length > 0
                    ? pkg.extractedSkus.map((sku) => `${sku.sku}${sku.quantity ? ` ×${sku.quantity}` : ''}`).join(', ')
                    : t('tracker.orders.packages.contentsUnknown')}
                  {pkg.internalReference && (
                    <><span aria-hidden="true"> · </span>{t('tracker.orders.packages.internalRef')}: {pkg.internalReference}</>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(canClientQcRedo || switchTargets.length > 0) && selectedLocation && (
        <div className="my-2 flex flex-wrap items-center justify-end gap-2">
          {switchTargets.map((to) => (
            <Button key={to} variant="outline" size="sm" onClick={() => setOrderSwitchTo(to)}>
              <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
              {t('tracker.printTechSwitch.order.menuItem', { tech: to })}
            </Button>
          ))}
          {orderSwitchTo && (
            <SwitchOrderPrintTechnologyDialog
              open={!!orderSwitchTo}
              onOpenChange={(open) => { if (!open) setOrderSwitchTo(null); }}
              target={{
                orderId,
                orderName: orderName ?? '',
                to: orderSwitchTo,
                locationId: selectedLocation.id,
                garments,
              }}
            />
          )}
          {canClientQcRedo && (
            <>
              <Button variant="outline" size="sm" onClick={() => setQcRedoOpen(true)}>
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                {t('tracker.clientQcRedo.button')}
              </Button>
              <ClientQcRedoDialog
                open={qcRedoOpen}
                onOpenChange={setQcRedoOpen}
                orderId={orderId}
                orderName={orderName ?? ''}
                locationId={selectedLocation.id}
                garments={qcRedoEligibleGarments(garments)}
                onDone={onClientQcRedoDone}
              />
            </>
          )}
        </div>
      )}
      {garments.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">{t('tracker.orders.noGarments')}</p>
      ) : (
        <TrackerGarmentTable garments={garments} orderId={orderId} canManageFloor={canManageFloor} />
      )}
    </ExpandedRowPanel>
  );
}
